import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';

/**
 * RegistrationTeam Schema
 * Represents a team's registration within a registration period
 */

// Team manager sub-schema
export const TeamManagerSchema = z.object({
  name: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  user: objectId,
});

export type TeamManager = z.infer<typeof TeamManagerSchema>;

// Team representative sub-schema
export const TeamRepresentativeSchema = z.object({
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  title: z.string().optional(),
  info: z.any().optional(), // Mixed type
});

export type TeamRepresentative = z.infer<typeof TeamRepresentativeSchema>;

// Message sub-schema
export const MessageSchema = z.object({
  date: z.coerce.date().default(() => new Date()),
  text: z.string().optional(),
});

// Registration player sub-schema
export const RegistrationPlayerEntrySchema = z.object({
  name: z.string().optional(),
  player: objectId,
  email: z.string().optional(),
  hasPaid: z.boolean().optional(),
  paidDate: z.coerce.date().optional(),
  paidBy: objectId, // User who paid for this player (player-pay-separately flow)
  info: z.any().optional(), // Mixed type
  user: objectId,
  messages: z.array(MessageSchema).default([]),
});

export type RegistrationPlayerEntry = z.infer<typeof RegistrationPlayerEntrySchema>;

// A Stripe Payment Link minted for this team. Links snapshot their amount at
// creation and never expire, so we record every minted link to be able to
// DEACTIVATE it — when a fresh link replaces it, and when the underlying
// payment product's pricing changes (stale links must not charge old amounts).
export const StripePaymentLinkRecordSchema = z.object({
  id: z.string(), // Stripe payment link id (plink_…)
  kind: z.string().optional(), // 'team' | 'player' | 'player-email'
  key: z.string().optional(), // per-player links: the player email
  // isTest (retired ADR 0045) is intentionally absent: legacy records carrying
  // it are purged by script 33; the sweep skips any stragglers.
  accountId: z.string().optional(), // connected account the link lives on
});

export type StripePaymentLinkRecord = z.infer<typeof StripePaymentLinkRecordSchema>;

// Config sub-schema
export const RegistrationTeamConfigSchema = z.object({
  disableSync: z.boolean().optional(),
});

// Pricing parameters frozen at the FIRST real payment (Stripe webhook or an
// admin-logged customPaidAmount credit). Once money lands, later edits to the
// shared PaymentProduct must not reprice this team — cost resolution
// (calculateTeamCost) and remaining per-player checkouts read these frozen
// params instead of the live product. Tier resolution (early-bird/late) still
// happens per unit at its own payment date, just against the frozen numbers.
// First write wins; absent = no payment yet, team floats with the live product.
export const PricingSnapshotSchema = z.object({
  paymentProduct: objectId, // product the params were copied from
  amount: z.number().optional(), // cents
  isFree: z.boolean().optional(),
  perPlayer: z.boolean().optional(),
  perTeam: z.boolean().optional(),
  playerPaySeparately: z.boolean().optional(),
  earlyBirdAmount: z.number().optional(),
  earlyBirdDeadline: z.coerce.date().optional(),
  lateRegistrationAmount: z.number().optional(),
  lateRegistrationStartDate: z.coerce.date().optional(),
  additionalTeamAmount: z.number().optional(),
  additionalTeamThreshold: z.number().optional(),
  stampedAt: z.coerce.date().optional(),
});

export type PricingSnapshot = z.infer<typeof PricingSnapshotSchema>;

export const RegistrationTeamSchema = z.object({
  _id: objectId,
  // Team managers (named)
  teamManager1: TeamManagerSchema.optional(),
  teamManager2: TeamManagerSchema.optional(),
  // Team managers/representatives (arrays)
  teamRepresentatives: z.array(TeamRepresentativeSchema).default([]),
  teamManagers: z.array(TeamRepresentativeSchema).default([]),
  // Config
  config: RegistrationTeamConfigSchema.optional(),
  // Team info
  teamName: z.string().optional(),
  info: z.any().optional(), // Mixed type
  // Admin lock-in: '' = registered, 'accepted' = officially placed in a
  // division (visible on site, can no longer be moved). NOT a capacity/waitlist
  // state — see packages/shared/utils/waitlist.ts:getCapacityState.
  status: z.string().default(''),
  contestType: z.string().optional(),
  teamInfo: z.any().optional(), // Mixed type
  signed: z.any().optional(), // Mixed type
  // Payment info
  hasPaid: z.boolean().optional(),
  paidDate: z.coerce.date().optional(),
  paidNotes: z.string().optional(),
  customPaidAmount: z.number().optional(),
  customPaidDate: z.coerce.date().optional(),
  customPaidBy: objectId, // Admin who recorded the custom payment
  customPaidByName: z.string().optional(), // denormalized admin name for display
  // Applied discount code (stamped by applyDiscountCode; ADR 0026). The
  // amount-off is locked in CENTS at apply time so later edits to the code
  // don't change what this team owes. discountRedeemed guards the
  // redemption-count increment (webhook or free-completion) firing once.
  discountCode: z.string().optional(),
  discountAmountOff: z.number().optional(),
  discountRedeemed: z.boolean().optional(),
  // Frozen pricing params — see PricingSnapshotSchema. Absent until the team's
  // first real payment; never overwritten once stamped.
  pricingSnapshot: PricingSnapshotSchema.optional(),
  isAccepted: z.boolean().optional(),
  // Admin acknowledgment that this team was invited to register AFTER the
  // registration's end date (Add Team → "Ignore registration deadline").
  // Record-keeping + future-proofing; the invited flow itself is not gated.
  allowLateRegistration: z.boolean().optional(),
  promotedAt: z.coerce.date().optional(), // legacy: set when promoted from waitlist (no longer written)
  // Notification marker for the derived capacity model: set true while a team is
  // 'waitlisted', cleared when it transitions to 'secured' (so the off-the-waitlist
  // email fires exactly once per promotion). Not a capacity input — see
  // packages/shared/utils/waitlist.ts:reconcileWaitlistNotifications.
  wasWaitlisted: z.boolean().optional(),
  // Players
  players: z.array(RegistrationPlayerEntrySchema).default([]),
  playerz: z.array(z.string()).default([]), // Array of RegistrationPlayer refs
  // Minted Stripe payment links still considered active (pruned on
  // deactivation) — see StripePaymentLinkRecordSchema.
  stripePaymentLinks: z.array(StripePaymentLinkRecordSchema).default([]),
  // Set by the public team form when the registrant ticks "I am a participant
  // in this tournament/league" (individual sports only). Drives auto-fill of
  // player #1 on the player-details page.
  isOwnerParticipant: z.boolean().optional(),
  // SMS consent front-load (INT-0015): captured OPTIONALLY on the public
  // team form so the audience is TCPA-ready before SMS ships. `smsConsentAt`
  // is stamped server-side in create_new when consent is given. No SMS is
  // actually sent anywhere yet — these are consent records only.
  smsConsent: z.boolean().optional(),
  smsConsentPhone: z.string().optional(),
  smsConsentAt: z.coerce.date().optional(),
  // Free-agent entry (ADR 0054): a person who registered WITHOUT a team.
  // A free-agent RegistrationTeam has exactly one Player (the registrant),
  // a zqtm_ auto name, an optional requestedDivision PREFERENCE, and NO
  // DivisionTeam until an admin assigns them. Free agents never consume a
  // division capacity seat (excluded inside getRegistrationTeamsForDivision)
  // and are excluded from public rosters, duplicate detection, and the
  // multi-team discount's prior-team count.
  isFreeAgent: z.boolean().optional(),
  // Where the admin placed this free agent (DivisionTeam), set by
  // assignFreeAgents alongside status: 'accepted'.
  assignedDivisionTeam: objectId,
  // Refs
  payment: objectId,
  registration: objectId,
  user: objectId,
  admin: objectId, // Admin who created this RegistrationTeam (admin-side flow)
  organization: objectId, // Organization ref
  tournament: objectId,
  league: objectId, // DEPRECATED — null/stale on modern teams. Use `organization` for the team's org (there's a resolveTeamOrg() helper: organization || league). Reading the org via this field silently loses the org's platform-fee exemption.
  contest: objectId, // League ref
  division: objectId,
  requestedDivision: objectId,
  requestedContest: objectId, // League ref
  team: objectId,
  divisionTeam: objectId,
  ...timestampFields,
});

export type RegistrationTeam = z.infer<typeof RegistrationTeamSchema>;

// Metadata for RegistrationTeam fields
export const registrationTeamMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  teamManager1: { label: 'Team Manager 1' },
  teamManager2: { label: 'Team Manager 2' },
  teamRepresentatives: { label: 'Team Representatives' },
  teamManagers: { label: 'Team Managers' },
  config: { hidden: true },
  teamName: { label: 'Team Name', editable: true },
  info: { hidden: true },
  status: { label: 'Admin Status', editable: true },
  isOwnerParticipant: { hidden: true },
  smsConsent: { label: 'SMS Consent', editable: false },
  smsConsentPhone: { label: 'SMS Consent Phone', editable: false },
  smsConsentAt: { label: 'SMS Consent At', editable: false },
  isFreeAgent: { label: 'Free Agent', hidden: true },
  assignedDivisionTeam: { ref: 'DivisionTeam', label: 'Assigned Team', hidden: true },
  contestType: { label: 'Contest Type' },
  teamInfo: { hidden: true },
  signed: { hidden: true },
  hasPaid: { label: 'Has Paid', editable: true },
  paidDate: { label: 'Paid Date', editable: true },
  paidNotes: { label: 'Paid Notes', editable: true },
  customPaidAmount: { label: 'Custom Paid Amount', editable: true },
  customPaidDate: { label: 'Custom Paid Date', editable: true },
  customPaidBy: { ref: 'Admin' },
  customPaidByName: { label: 'Custom Paid By', editable: false },
  discountCode: { label: 'Discount Code', editable: false },
  discountAmountOff: { label: 'Discount Amount Off (cents)', editable: false },
  discountRedeemed: { label: 'Discount Redeemed', hidden: true },
  pricingSnapshot: { label: 'Pricing Snapshot', hidden: true },
  isAccepted: { label: 'Is Accepted', editable: true },
  allowLateRegistration: { label: 'Allow Late Registration', editable: true },
  promotedAt: { label: 'Promoted At', editable: false },
  wasWaitlisted: { label: 'Was Waitlisted', hidden: true },
  players: { label: 'Players' },
  playerz: { ref: 'RegistrationPlayer', label: 'Registration Players' },
  payment: { ref: 'Payment', label: 'Payment' },
  registration: { ref: 'Registration', label: 'Registration' },
  user: { ref: 'User', label: 'User' },
  admin: { ref: 'Admin', label: 'Admin' },
  organization: { ref: 'Organization', hidden: true },
  tournament: { ref: 'Tournament', label: 'Tournament' },
  league: { ref: 'League', label: 'League' },
  contest: { ref: 'League', label: 'Contest' },
  division: { ref: 'Division', label: 'Division' },
  requestedDivision: { ref: 'Division', label: 'Requested Division' },
  requestedContest: { ref: 'League', label: 'Requested Contest' },
  team: { ref: 'Team', label: 'Team' },
  divisionTeam: { ref: 'DivisionTeam', label: 'Division Team' },
  created: { hidden: true },
  updated: { hidden: true },
};

// Metadata for teamManager sub-document
export const teamManagerMetadata: Record<string, FieldMetadata> = {
  user: { ref: 'User' },
};

// Metadata for player entry sub-document
export const registrationPlayerEntryMetadata: Record<string, FieldMetadata> = {
  player: { ref: 'Player' },
  user: { ref: 'User' },
  paidBy: { ref: 'User' },
};
